import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button as MuiButton,
  Snackbar,
  Alert,
  IconButton,
  Tooltip as MuiTooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  TextField,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FeatherIcon from 'feather-icons-react';
import { useParams } from 'react-router-dom';
import { SignatureCreator } from '../../NewMasterSheet/LeaseProcess/Flow/StepperComponents/lease/components/steps/components';
import { approveSOA, rejectSOA, getCompanyByPropertyId, getSignedUrl, getSOADetailsByToken } from '../api';
import {
  Card,
  CardBody,
  CardTitle,
  CardText,
  Button,
  Tooltip,
  Container,
} from 'reactstrap';
import { getSOAInitialValues } from '../Generate/Components/FormikHelpers';
import DeductionInvoiceRef from '../Generate/Components/DeductionInvoiceRef';
import { Previewer } from '../../../../components/DocumentPreviewer/Previewer';
import './soa.scss';
import Swal from 'sweetalert2';

const formatAmount = (value) => {
  if (!value) return '$0.00';
  const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  const absValue = Math.abs(numValue).toFixed(2);
  return numValue < 0 ? `-$${absValue}` : `$${absValue}`;
};

const extractS3KeyAndType = (invoice) => {
  if (!invoice) return { key: '', fileType: '' };

  const urlPattern = /^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/;

  if (typeof invoice === 'string') {
    const match = invoice.match(urlPattern);
    const key = match ? match[1] : invoice;
    const fileType = key.split('.').pop()?.toLowerCase() || '';
    return { key, fileType };
  }

  const keyOrUrl = invoice.key || invoice.url || '';
  const match = keyOrUrl.match(urlPattern);
  const key = match ? match[1] : keyOrUrl;
  const fileType =
    invoice.fileType?.toLowerCase() ||
    key.split('.').pop()?.toLowerCase() || '';

  return { key, fileType };
};

const ReadOnlyField = ({ label, value, icon, className = '' }) => (
  <>
    <Box className={`soa-editable-field ${className}`} sx={{ width: '100%' }}>
      <Box className="soa-display-value" sx={{ width: '100%' }}>
        <Typography variant="body2" className="soa-field-text">
          {value || 'N/A'}
        </Typography>
      </Box>
    </Box>
    {label && (
      <Typography
        variant="caption"
        className="soa-field-subtitle"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        {icon && <FeatherIcon icon={icon} size={16} className="soa-edit-icon" />}
        {`(${label})`}
      </Typography>
    )}
  </>
);

const ApprovalSection = ({ approved, rejected, approving, rejecting, handleApproveClick, handleRejectClick, actionTimestamp }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [rejectTooltipOpen, setRejectTooltipOpen] = useState(false);
  const approveButtonRef = useRef(null);
  const rejectButtonRef = useRef(null);

  const getStatusBadge = () => {
    if (approved) return { color: 'success', icon: 'check-circle', text: 'Approved' };
    if (rejected) return { color: 'danger', icon: 'x-circle', text: 'Rejected' };
    return { color: 'warning', icon: 'clock', text: 'Pending Review' };
  };

  const status = getStatusBadge();

  const formattedTimestamp = actionTimestamp
    ? new Date(actionTimestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    : 'N/A';

  return (
    <Container className="soa-action-buttons d-flex justify-content-center align-items-center my-4" style={{ maxWidth: '700px' }}>
      <Card
        className={`shadow ${approved ? 'border-success' : rejected ? 'border-danger' : 'border-secondary'}`}
        style={{ borderWidth: '2px', borderRadius: '12px' }}
      >
        <CardBody className="text-center">
          <div className="mb-4 text-center">
            <Badge
              color={status.color}
              pill
              className="d-flex align-items-center gap-2 px-3 py-2"
            >
              <FeatherIcon icon={status.icon} size={18} />
              {status.text}
            </Badge>
          </div>
          <CardTitle tag="h4" className="d-flex align-items-center justify-content-center gap-2 mb-3">
            <FeatherIcon
              icon="file-text"
              size={24}
              className={approved ? 'text-success' : rejected ? 'text-danger' : 'text-secondary'}
            />
            Statement of Account {approved ? 'Approved' : rejected ? 'Rejected' : 'Review'}
          </CardTitle>
          <CardText className="text-muted mb-4">
            {approved && 'Your digital signature has been successfully applied to this Statement of Account. The document is now legally binding.'}
            {rejected && 'This Statement of Account has been rejected. Please review the rejection comments and create a new SOA if necessary.'}
            {!approved && !rejected && 'Please carefully review the Statement of Account details above and choose to approve or reject this document.'}
            {!approved && !rejected && (
              <p className="text-center text-muted small mt-3">
                By clicking "Approve & Sign", you agree to the terms and conditions outlined in this statement.
              </p>
            )}
            {(approved || rejected) && (
              <p className="text-center text-muted small mt-3">
                {approved ? 'You have already approved this document.' : 'You have already rejected this document.'} No further action is required.
              </p>
            )}
          </CardText>
          <hr className="w-25 mx-auto my-4" />

          {approved ? (
            <div className="d-flex justify-content-center align-items-center">
              <Button
                color="success"
                outline
                disabled
                className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                style={{ minWidth: '200px' }}
              >
                <FeatherIcon icon="check-circle" size={18} />
                Document Approved
              </Button>
            </div>
          ) : rejected ? (
            <div className="d-flex justify-content-center align-items-center">
              <Button
                color="danger"
                outline
                disabled
                className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                style={{ minWidth: '200px' }}
              >
                <FeatherIcon icon="x-circle" size={18} />
                Document Rejected
              </Button>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center gap-3">
              <Button
                id="approveButton"
                ref={approveButtonRef}
                color="success"
                disabled={approving || rejecting}
                onClick={handleApproveClick}
                className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                style={{ minWidth: '180px' }}
              >
                {approving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="shield" size={18} />
                    Approve & Sign
                  </>
                )}
              </Button>

              <Button
                id="rejectButton"
                ref={rejectButtonRef}
                color="danger"
                outline
                disabled={approving || rejecting}
                onClick={handleRejectClick}
                className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                style={{ minWidth: '150px' }}
              >
                {rejecting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <FeatherIcon icon="x-circle" size={18} />
                    Reject
                  </>
                )}
              </Button>
            </div>
          )}

          {approveButtonRef.current && !approved && (
            <Tooltip
              placement="top"
              isOpen={tooltipOpen}
              target="approveButton"
              toggle={() => setTooltipOpen(!tooltipOpen)}
            >
              Click to review and digitally sign this document
            </Tooltip>
          )}
          {rejectButtonRef.current && !rejected && (
            <Tooltip
              placement="top"
              isOpen={rejectTooltipOpen}
              target="rejectButton"
              toggle={() => setRejectTooltipOpen(!rejectTooltipOpen)}
            >
              Click to reject this document with optional comments
            </Tooltip>
          )}

          {(approved || rejected) && (
            <Badge
              color={approved ? 'success' : 'danger'}
              className="position-absolute top-0 end-0 translate-middle badge rounded-circle p-2"
              style={{ transform: 'translate(50%, -50%)' }}
            >
              <FeatherIcon icon={approved ? 'check-circle' : 'x-circle'} size={24} />
            </Badge>
          )}
        </CardBody>
      </Card>

      {(approved || rejected) && (
        <Card className={`mt-3 ${approved ? 'border-success' : 'border-danger'}`}>
          <CardBody className="text-center">
            <div className={`d-flex align-items-center justify-content-center gap-2 ${approved ? 'text-success' : 'text-danger'}`}>
              <FeatherIcon icon={approved ? 'check-circle' : 'x-circle'} size={18} />
              <span className="font-weight-bold">
                {approved ? 'Successfully approved and digitally signed' : 'Document has been rejected'}
              </span>
            </div>
            <CardText className={`${approved ? 'text-success' : 'text-danger'} small mt-1`}>
              Timestamp: {formattedTimestamp}
            </CardText>
          </CardBody>
        </Card>
      )}
    </Container>
  );
};

// Invoices behind the deductions are internal: off by default, because this
// page is what tenants open from their approval link. Staff screens
// (AuditorApprovalTable) opt in explicitly.
const ApproveStatementOfAccount = ({ showInvoices = false, soaId: propSoaId }) => {
  const { soaId: paramSoaId, token } = useParams();
  const soaId = propSoaId || paramSoaId;
  const [soaData, setSOAData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSignatureCreatorOpen, setIsSignatureCreatorOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [approved, setApproved] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [actionTimestamp, setActionTimestamp] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedInvoiceUrl, setSelectedInvoiceUrl] = useState('');
  const [selectedFileType, setSelectedFileType] = useState('');
  const [selectedDeductionIndex, setSelectedDeductionIndex] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionComments, setRejectionComments] = useState('');

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const propertyId = soaData?.property_details?.property_id;
        if (propertyId) {
          const response = await getCompanyByPropertyId(propertyId);
          setCompanyDetails(response);
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      }
    };
    if (soaData?.property_details?.property_id) {
      fetchCompanyDetails();
    }
  }, [soaData?.property_details?.property_id]);

  useEffect(() => {
    const fetchSOAData = async () => {
      try {
        setLoading(true);
        const transformedData = await getSOAInitialValues(soaId, token);
        const rawResponse = await getSOADetailsByToken(soaId, token);

        if (!rawResponse.success) {
          setError(rawResponse.message || 'Failed to load SOA details.');
          return;
        }

        setSOAData(transformedData);

        const userEmail = rawResponse.currentUserEmail || rawResponse.data?.currentUserEmail;
        if (!userEmail) {
          setError('User email not found. Please ensure you are logged in.');
          return;
        }

        setCurrentUserEmail(userEmail);
        const userApproval = transformedData.approvals?.find(
          approval => approval.email?.toLowerCase() === userEmail?.toLowerCase()
        );

        if (userApproval?.status === 'approved') {
          setApproved(true);
          setActionTimestamp(userApproval.updated_at);
        } else if (userApproval?.status === 'rejected') {
          setRejected(true);
          setActionTimestamp(userApproval.updated_at);
        }
      } catch (err) {
        console.error('Error fetching SOA data:', err);
        setError(err.message || 'Failed to load SOA details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSOAData();
  }, [soaId, token]);

  const totalDeductions = useMemo(() => {
    if (!soaData?.account_details?.deductions) return 0;
    return soaData.account_details.deductions.reduce(
      (sum, deduction) => sum + (Number(deduction.cost_per_action) || 0) * (Number(deduction.number_of_actions) || 1),
      0
    );
  }, [soaData?.account_details?.deductions]);

  const balance = useMemo(() => {
    const totalSecurity = Number(soaData?.account_details?.security) || 0;
    const totalInterest = Number(soaData?.account_details?.interest) || 0;
    const earlyMoveOutProrate = Number(soaData?.account_details?.early_move_out_prorate) || 0;
    return totalSecurity + totalInterest + earlyMoveOutProrate - totalDeductions;
  }, [soaData?.account_details?.security, soaData?.account_details?.interest, soaData?.account_details?.early_move_out_prorate, totalDeductions]);

  const handleSignatureCreated = async ({ signatureData }) => {
    if (approved) {
      setSnackbar({
        open: true,
        message: 'This SOA has already been approved by you.',
        severity: 'info',
      });
      setIsSignatureCreatorOpen(false);
      return;
    }
    try {
      setApproving(true);
      const approvalData = { signature: signatureData };
      const response = await approveSOA(soaId, token, approvalData);

      if (response.success) {
        setApproved(true);
        setActionTimestamp(new Date().toISOString());
        setIsSignatureCreatorOpen(false);
        setSnackbar({
          open: true,
          message: response.message || 'SOA has been successfully approved!',
          severity: 'success',
        });
      } else {
        throw new Error(response.message || 'Approval failed');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to approve SOA. Please try again.',
        severity: 'error',
      });
    } finally {
      setApproving(false);
    }
  };

  const handleApproveClick = () => {
    if (approved || rejected) {
      setSnackbar({
        open: true,
        message: 'You have already taken action on this SOA.',
        severity: 'info',
      });
      return;
    }
    setIsSignatureCreatorOpen(true);
  };

  const handleRejectClick = () => {
    if (approved || rejected) {
      setSnackbar({
        open: true,
        message: 'You have already taken action on this SOA.',
        severity: 'info',
      });
      return;
    }
    setRejectionComments('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    try {
      setRejecting(true);
      const rejectionData = {
        comments: rejectionComments.trim(),
        rejectedBy: currentUserEmail,
        rejectedAt: new Date().toISOString(),
      };

      const response = await rejectSOA(soaId, token, rejectionData);

      if (response.success) {
        setRejected(true);
        setActionTimestamp(new Date().toISOString());
        setSnackbar({
          open: true,
          message: response.message || 'SOA has been rejected successfully.',
          severity: 'warning',
        });
        setRejectDialogOpen(false);

        await Swal.fire({
          title: 'SOA Rejected',
          text: rejectionComments
            ? `The Statement of Account has been rejected with your comments: "${rejectionComments}"`
            : 'The Statement of Account has been rejected.',
          icon: 'success',
          confirmButtonColor: '#3085d6',
        });
      } else {
        throw new Error(response.message || 'Rejection failed');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to reject SOA. Please try again.',
        severity: 'error',
      });

      await Swal.fire({
        title: 'Rejection Failed',
        text: err.message || 'Failed to reject SOA. Please try again.',
        icon: 'error',
        confirmButtonColor: '#d33',
      });
    } finally {
      setRejecting(false);
    }
  };

  const handleViewInvoice = async (index) => {
    const deductions = soaData.account_details?.deductions || [];
    if (!deductions || index < 0 || index >= deductions.length) {
      setSnackbar({ open: true, message: 'Invalid deduction selected.', severity: 'error' });
      return;
    }

    const deduction = deductions[index];
    if (!deduction.invoice || (typeof deduction.invoice !== 'string' && !deduction.invoice.url) || (typeof deduction.invoice === 'string' && deduction.invoice.trim() === '')) {
      setSnackbar({ open: true, message: 'No invoice available to view.', severity: 'error' });
      return;
    }

    try {
      const { key, fileType } = extractS3KeyAndType(deduction.invoice);
      if (!key) throw new Error('Invalid invoice key');
      const response = await getSignedUrl(key, 'get', token, soaId);
      if (response.success && response.data.signedUrl) {
        setSelectedInvoiceUrl(response.data.signedUrl);
        setSelectedFileType(fileType);
        setSelectedDeductionIndex(index);
        setPreviewDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Failed to load the document for preview.', severity: 'error' });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'An error occurred while loading the document.', severity: 'error' });
    }
  };

  const handlePreviewClose = () => {
    setPreviewDialogOpen(false);
    setSelectedInvoiceUrl('');
    setSelectedFileType('');
    setSelectedDeductionIndex(null);
  };

  const displayDeductions = soaData?.account_details?.deductions || [];

  if (loading) {
    return (
      <Box className="soa-form-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></Box>
          <Typography variant="body1">Loading SOA details...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="soa-form-container" sx={{ maxWidth: '960px', mx: 'auto', p: 3 }}>
        <Paper elevation={2} className="soa-document-wrapper">
          <Alert severity="error" icon={<FeatherIcon icon="x" size={20} />}>
            <Typography variant="h6">Error</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (!soaData) {
    return (
      <Box className="soa-form-container" sx={{ maxWidth: '960px', mx: 'auto', p: 3 }}>
        <Paper elevation={2} className="soa-document-wrapper">
          <Alert severity="warning" icon={<FeatherIcon icon="file-text" size={20} />}>
            <Typography variant="h6">Warning</Typography>
            <Typography variant="body2">SOA not found.</Typography>
          </Alert>
        </Paper>
      </Box>
    );
  }

  const tenantDisplay = soaData.tenant
    ?.map(t => {
      const name = t.name || '';
      const email = t.email && t.email.toLowerCase() !== 'n/a' ? t.email : '';
      return email ? `${name} (${email})` : name;
    })
    .filter(Boolean)
    .join(', ') || '';

  return (
    <Box className="soa-form-container">
      <div className="mobile-overflow-wrapper">
        <Paper elevation={2} className="soa-document-wrapper">
          <Box className="soa-document-header">
            {companyDetails && (
              <>
                {companyDetails.logo && (
                  <img
                    src={companyDetails.logo}
                    alt={`${companyDetails.name} Logo`}
                    className="soa-company-logo"
                    style={{ maxHeight: '50px', maxWidth: '150px', marginBottom: '10px' }}
                  />
                )}
                <Typography
                  variant="h5"
                  className="soa-company-name"
                  sx={{ fontWeight: 'bold', marginBottom: '10px' }}
                >
                  {companyDetails.name}
                </Typography>
              </>
            )}
            <Typography variant="h4" className="soa-main-title">
              Landlord's Notice to Tenant
            </Typography>
            <Typography variant="h3" className="soa-document-title">
              Security Deposit — Statement of Account
            </Typography>
            <Typography variant="body2" className="soa-legal-notice">
              This must be forwarded to tenant within 10 days of the tenant giving up possession.
              <br />
              See section 45 and 46 of <em>The Residential Tenancies Act</em>
            </Typography>
          </Box>

          {(approved || rejected) && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Alert severity={approved ? 'success' : 'error'} icon={<FeatherIcon icon={approved ? 'check-circle' : 'x-circle'} size={20} />}>
                <Typography variant="body1">
                  This SOA has been {approved ? 'approved' : 'rejected'}
                </Typography>
              </Alert>
            </Box>
          )}

          <Box className="soa-info-section">
            <Box className="soa-info-grid">
              <Box className="soa-tenant-name-section">
                <Box className="soa-tenant-row unified-tenant-row">
                  <Typography variant="body1" className="soa-field-label" sx={{ marginRight: '8px' }}>TO:</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                    <ReadOnlyField
                      label="Name and Email of Tenant(s)"
                      value={tenantDisplay}
                      icon="user"
                      className="soa-tenant-name"
                    />
                  </Box>
                </Box>
              </Box>

              <Box className="soa-address-section">
                <Box className="soa-address-row">
                  <Typography variant="body1" className="soa-field-label">AT:</Typography>
                  <Box className="soa-address-column">
                    <ReadOnlyField
                      label="Unit Number"
                      value={soaData.property_details?.unit_no}
                      className="soa-address-field"
                    />
                  </Box>
                  <Box className="soa-address-column street">
                    <ReadOnlyField
                      label="Street Address"
                      value={soaData.address?.street}
                      className="soa-address-field"
                    />
                  </Box>
                  <Box className="soa-address-column">
                    <ReadOnlyField
                      label="City"
                      value={soaData.address?.city}
                      className="soa-address-field"
                    />
                  </Box>
                  <Box className="soa-address-column">
                    <ReadOnlyField
                      label="Province"
                      value={soaData.address?.province}
                      className="soa-address-field"
                    />
                  </Box>
                  <Box className="soa-address-column">
                    <ReadOnlyField
                      label="Postal Code"
                      value={soaData.address?.postal_code}
                      className="soa-address-field"
                    />
                  </Box>
                  <Box className="soa-address-column">
                    <ReadOnlyField
                      label="Country"
                      value={soaData.address?.country}
                      className="soa-address-field"
                    />
                  </Box>
                </Box>
              </Box>

              <Box className="soa-rental-period">
                <Typography variant="h6" className="soa-section-title">Rental Period</Typography>
                <Box className="soa-rental-row">
                  <Typography variant="body1" className="soa-field-label">FROM:</Typography>
                  <Box className="soa-rental-column">
                    <ReadOnlyField
                      label="Start Date"
                      value={soaData.rental_period?.start_date ? new Date(soaData.rental_period.start_date).toLocaleDateString() : ''}
                      icon="calendar"
                      className="soa-rental-field"
                    />
                  </Box>
                  <Typography variant="body1" className="soa-field-label">TO:</Typography>
                  <Box className="soa-rental-column">
                    <ReadOnlyField
                      label="End Date"
                      value={soaData.rental_period?.end_date ? new Date(soaData.rental_period.end_date).toLocaleDateString() : ''}
                      icon="calendar"
                      className="soa-rental-field"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className="soa-deposit-section">
            <Box className="soa-deposit-container">
              <Typography
                variant="h6"
                className="soa-section-title text-center"
              >
                Security Deposit Summary
              </Typography>
              <Paper variant="outlined" className="soa-deposit-summary">
                <Box className="card-content">
                  <Box className="card-header">
                    <Typography variant="body1" className="total-deposit">
                      Total Security Deposit Received: {formatAmount(soaData.account_details?.security)}
                    </Typography>
                  </Box>
                  {soaData.account_details?.interest > 0 && (
                    <Box className="card-header">
                      <Typography variant="body1" className="interest">
                        Interest: {formatAmount(soaData.account_details?.interest)}
                      </Typography>
                    </Box>
                  )}
                  {soaData.account_details?.early_move_out_prorate > 0 && (
                    <Box className="card-header">
                      <Typography variant="body1" className="early-move-out-prorate">
                        Emo Pro Rate: {formatAmount(soaData.account_details.early_move_out_prorate)}
                      </Typography>
                    </Box>
                  )}

                  <Box className="soa-deductions-section">
                    <Box
                      className="soa-deductions-container"
                      sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}
                    >
                      <Typography
                        variant="h6"
                        className="soa-section-title"
                        sx={{ mb: 2, fontWeight: 'medium' }}
                      >
                        Less Deductions, as Listed:
                      </Typography>
                      <Paper
                        variant="outlined"
                        className="soa-deductions-table"
                        sx={{ borderRadius: 2, overflow: 'auto' }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell
                                className="soa-table-header"
                                sx={{ width: '5%', fontWeight: 'bold' }}
                              >
                                No.
                              </TableCell>
                              <TableCell
                                className="soa-table-header"
                                sx={{ width: '30%', fontWeight: 'bold' }}
                              >
                                Description
                              </TableCell>
                              <TableCell
                                className="soa-table-header"
                                sx={{ width: '15%', fontWeight: 'bold' }}
                              >
                                Cost Per Action
                              </TableCell>
                              <TableCell
                                className="soa-table-header"
                                sx={{ width: '15%', fontWeight: 'bold' }}
                              >
                                Quantity
                              </TableCell>
                              <TableCell
                                className="soa-table-header"
                                sx={{ width: '15%', fontWeight: 'bold' }}
                              >
                                Total
                              </TableCell>
                              {showInvoices && (
                                <TableCell
                                  className="soa-table-header"
                                  sx={{ width: '20%', fontWeight: 'bold' }}
                                >
                                  Invoice
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {displayDeductions.length > 0 ? (
                              displayDeductions.map((deduction, index) => (
                                <TableRow
                                  key={deduction.id || `deduction-${index}`}
                                  className="soa-deduction-row"
                                >
                                  <TableCell className="soa-table-cell" sx={{ width: '5%' }}>
                                    {index + 1}
                                  </TableCell>
                                  <TableCell className="soa-table-cell" sx={{ width: '30%' }}>
                                    {deduction.name || 'N/A'}
                                  </TableCell>
                                  <TableCell className="soa-table-cell" sx={{ width: '15%' }}>
                                    {formatAmount(Number(deduction.cost_per_action) || 0)}
                                  </TableCell>
                                  <TableCell className="soa-table-cell" sx={{ width: '15%' }}>
                                    {Number(deduction.number_of_actions) || 0}
                                  </TableCell>
                                  <TableCell className="soa-table-cell soa-calculated-total" sx={{ width: '15%' }}>
                                    {formatAmount(
                                      (Number(deduction.cost_per_action) || 0) *
                                      (Number(deduction.number_of_actions) || 1)
                                    )}
                                  </TableCell>
                                  {showInvoices && (
                                    <TableCell className="soa-table-cell soa-invoice-cell" sx={{ width: '20%' }}>
                                      {deduction.invoice_ref?.invoice_id ? (
                                        <DeductionInvoiceRef invoiceRef={deduction.invoice_ref} />
                                      ) : deduction.invoice && (typeof deduction.invoice === 'string' || deduction.invoice.url) && (typeof deduction.invoice === 'string' ? deduction.invoice.trim() !== '' : deduction.invoice.url.trim() !== '') ? (
                                        <MuiTooltip title={deduction.invoice.description ? `View invoice: ${deduction.invoice.description}` : 'View invoice'}>
                                          <Badge
                                            badgeContent={<span className="soa-invoice-badge">1</span>}
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                          >
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => handleViewInvoice(index)}
                                              aria-label="View uploaded invoice"
                                              sx={{
                                                bgcolor: 'primary.light',
                                                '&:hover': {
                                                  bgcolor: 'primary.main',
                                                  color: 'white',
                                                  transform: 'scale(1.1)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                                borderRadius: '8px',
                                              }}
                                            >
                                              <AttachFileIcon fontSize="small" />
                                            </IconButton>
                                          </Badge>
                                        </MuiTooltip>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary">
                                          No Invoice
                                        </Typography>
                                      )}
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={showInvoices ? 6 : 5} sx={{ textAlign: 'center', py: 4 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    No deductions added.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Paper>
                      <Box className="soa-totals-summary">
                        <Typography variant="body1" className="soa-total-deductions">
                          Total Deductions: {formatAmount(totalDeductions)}
                        </Typography>
                        <Typography
                          variant="body1"
                          className={`soa-total-balance ${balance >= 0 ? 'positive' : 'negative'}`}
                        >
                          {balance >= 0
                            ? `Please return ${formatAmount(Math.abs(balance))} to the Tenant`
                            : `Please collect ${formatAmount(Math.abs(balance))} from the Tenant for the Landlord`}
                        </Typography>
                      </Box>
                    </Box>

                    {soaData.cheque_enclosed?.status && (
                      <Box className="soa-payment-section">
                        <Box className="soa-payment-row">
                          <Box className="soa-payment-option">
                            <Box className="soa-cheque-option">
                              <Checkbox
                                checked={soaData.cheque_enclosed?.status}
                                className="soa-checkbox"
                                disabled
                                color="primary"
                              />
                              <Typography variant="body2" className="soa-cheque-text">
                                Cheque enclosed for
                              </Typography>
                              <Typography variant="body2" className="soa-cheque-amount">
                                {formatAmount(soaData.cheque_enclosed.amount)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box className="soa-landlord-payment">
                            <Typography variant="body2" className="soa-landlord-text">
                              Please pay Landlord:
                            </Typography>
                            <Typography variant="body2" className="soa-landlord-amount">
                              {formatAmount(balance >= 0 ? balance : 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>

          <Box className="soa-footer-section">
            <Box className="soa-footer-grid">
              <Box className="soa-footer-left">
                <Typography variant="h6" className="soa-footer-title">Statement Mailed to</Typography>
                <Box className="soa-row">
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Name and Email of Tenant(s)"
                      value={tenantDisplay}
                      className="soa-name"
                    />
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Address"
                      value={soaData.statement_details?.address}
                      className="soa-name"
                    />
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Typography variant="body2" className="soa-field-label mt-2">Date Mailed:</Typography>
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Date Mailed"
                      value={soaData.statement_details?.mailed_on ? new Date(soaData.statement_details.mailed_on).toLocaleDateString() : ''}
                      className="soa-name"
                    />
                  </Box>
                </Box>
              </Box>
              <Box className="soa-footer-right">
                <Typography variant="h6" className="soa-footer-title">Name and Address of Landlord/Agent:</Typography>
                <Box className="soa-row">
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Name of Landlord/Agent"
                      value={soaData.landlord_details?.name}
                      icon="user"
                      className="soa-name"
                    />
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Address"
                      value={soaData.landlord_details?.address}
                      icon="map-pin"
                      className="soa-name"
                    />
                  </Box>
                </Box>
                <Box className="soa-row">
                  <Typography variant="body2" className="soa-field-label mt-2">Phone Number:</Typography>
                  <Box className="soa-column">
                    <ReadOnlyField
                      label="Phone"
                      value={soaData.landlord_details?.phone}
                      icon="phone"
                      className="soa-name"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {soaData.landlord_details?.signature && (
            <Box className="soa-signature-section">
              <Box className="soa-section-content" sx={{ padding: '16px' }}>
                <img
                  src={soaData.landlord_details.signature}
                  alt="Landlord Signature"
                  className="soa-signature-field"
                  style={{ maxHeight: '100px', maxWidth: '250px', objectFit: 'contain' }}
                />
                <Typography variant="caption" className="soa-signature-subtitle">
                  Signature of Landlord or Landlord's Agent
                </Typography>
              </Box>
            </Box>
          )}

          {token && (
            <ApprovalSection
              approved={approved}
              rejected={rejected}
              approving={approving}
              rejecting={rejecting}
              handleApproveClick={handleApproveClick}
              handleRejectClick={handleRejectClick}
              actionTimestamp={actionTimestamp}
            />
          )}

          <SignatureCreator
            isOpen={isSignatureCreatorOpen}
            onClose={() => setIsSignatureCreatorOpen(false)}
            onSign={handleSignatureCreated}
            onSignAndSave={handleSignatureCreated}
            initialSignature=""
            width={600}
            height={200}
            disableSignAndSave={true}
          />

          <Dialog
            open={previewDialogOpen}
            onClose={handlePreviewClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="preview-invoice-dialog-title"
          >
            <DialogTitle id="preview-invoice-dialog-title">
              Invoice Preview
            </DialogTitle>
            <DialogContent>
              {soaData.account_details?.deductions?.[selectedDeductionIndex]?.invoice?.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Description:
                  </Typography>
                  <Typography variant="body2">
                    {soaData.account_details?.deductions?.[selectedDeductionIndex]?.invoice?.description}
                  </Typography>
                </Box>
              )}
              <Previewer
                signedUrl={selectedInvoiceUrl}
                type={selectedFileType}
                height="70vh"
                width="100%"
              />
            </DialogContent>
            <DialogActions>
              <MuiButton
                onClick={handlePreviewClose}
                color="primary"
                aria-label="Close preview"
              >
                Close
              </MuiButton>
            </DialogActions>
          </Dialog>

          <Dialog
            open={rejectDialogOpen}
            onClose={() => setRejectDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            aria-labelledby="reject-dialog-title"
            PaperProps={{
              sx: {
                borderRadius: '12px',
                padding: '24px',
                overflowY: 'visible',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                fontFamily: "'Times New Roman', Times, serif !important",
                maxWidth: '500px',
              },
            }}
          >
            <DialogTitle
              id="reject-dialog-title"
              sx={{
                padding: '0 0 16px 0',
                fontFamily: "'Times New Roman', Times, serif !important",
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#333',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              Reject Statement of Account
            </DialogTitle>
            <DialogContent
              sx={{
                overflowY: 'visible',
                padding: '24px 0',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "'Times New Roman', Times, serif !important",
                  fontSize: '1rem',
                  color: '#666',
                  marginBottom: '16px',
                  lineHeight: 1.6,
                }}
              >
                Are you sure you want to reject this Statement of Account?
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: "'Times New Roman', Times, serif !important",
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '8px',
                }}
              >
                Comments (Optional):
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                placeholder="Please provide reasons for rejection (optional)..."
                value={rejectionComments}
                onChange={(e) => setRejectionComments(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontFamily: "'Times New Roman', Times, serif !important",
                    fontSize: '1rem',
                    backgroundColor: '#f9f9f9',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "'Times New Roman', Times, serif !important",
                  fontSize: '0.875rem',
                  color: '#888',
                  marginTop: '8px',
                  display: 'block',
                  lineHeight: 1.4,
                }}
              >
                These comments will be saved for future reference.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                padding: '16px 0 0',
                justifyContent: 'flex-end',
                gap: '12px',
              }}
            >
              <MuiButton
                onClick={() => setRejectDialogOpen(false)}
                color="primary"
                disabled={rejecting}
                sx={{
                  fontFamily: "'Times New Roman', Times, serif !important",
                  fontSize: '1rem',
                  textTransform: 'none',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: '1px solid #1976d2',
                  color: '#1976d2',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    borderColor: '#1565c0',
                  },
                  '&:disabled': {
                    borderColor: '#bdbdbd',
                    color: '#bdbdbd',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Cancel
              </MuiButton>
              <MuiButton
                onClick={handleRejectConfirm}
                color="error"
                variant="contained"
                disabled={rejecting}
                autoFocus
                sx={{
                  fontFamily: "'Times New Roman', Times, serif !important",
                  fontSize: '1rem',
                  textTransform: 'none',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {rejecting ? (
                  <>
                    <span
                      className="animate-spin inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Processing...
                  </>
                ) : (
                  'Yes, Reject'
                )}
              </MuiButton>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </div>
    </Box>
  );
};

export { ApproveStatementOfAccount };